import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Carousel, CarouselContent, CarouselItem, CarouselNext, CarouselPrevious } from "@/components/ui/carousel";
import Autoplay from "embla-carousel-autoplay";
import facadeRef1 from "@/assets/facade-ref-1.jpg";
import facadeRef2 from "@/assets/facade-ref-2.webp";
import facadeRef3 from "@/assets/facade-ref-3.png";
import facadeRef4 from "@/assets/facade-ref-4.jpg";
import facadeRef5 from "@/assets/facade-ref-5.jpg";
import facadeRef6 from "@/assets/facade-ref-6.jpg";
import facadeRef7 from "@/assets/facade-ref-7.jpg";
import facadeRef8 from "@/assets/facade-ref-8.jpg";
import facadeRef9 from "@/assets/facade-ref-9.jpeg";
import facadeRef10 from "@/assets/facade-ref-10.jpeg";
import bathroomRef1 from "@/assets/bathroom-ref-1.jpg";
import bathroomRef2 from "@/assets/bathroom-ref-2.jpg";
import bathroomRef3 from "@/assets/bathroom-ref-3.jpg";
import bathroomRef4 from "@/assets/bathroom-ref-4.jpg";
import bathroomRef5 from "@/assets/bathroom-ref-5.jpg";
import bathroomRef6 from "@/assets/bathroom-ref-6.jpg";
import bathroomRef7 from "@/assets/bathroom-ref-7.webp";
import bathroomRef8 from "@/assets/bathroom-ref-8.jpg";
import bathroomRef9 from "@/assets/bathroom-ref-9.webp";
import floorImg from "@/assets/floor.jpg";
import specialSurfaceImg from "@/assets/special-surface.jpg";
import teamWorkImg from "@/assets/team-work.jpg";
import teamInnenarbeitenImg from "@/assets/team-innenarbeiten.webp";
import surfaceRef1 from "@/assets/surface-ref-1.webp";
import surfaceRef2 from "@/assets/surface-ref-2.webp";
import surfaceRef3 from "@/assets/surface-ref-3.jpg";
import surfaceRef4 from "@/assets/surface-ref-4.jpg";
import surfaceRef5 from "@/assets/surface-ref-5.jpeg";
import surfaceRef6 from "@/assets/surface-ref-6.jpeg";
import floorRef1 from "@/assets/floor-ref-1.jpeg";
import floorRef2 from "@/assets/floor-ref-2.jpg";
import floorRef3 from "@/assets/floor-ref-3.webp";
import floorRef4 from "@/assets/floor-ref-4.webp";
import interiorRef1 from "@/assets/interior-ref-1.jpg";
import interiorRef2 from "@/assets/interior-ref-2.jpeg";
import interiorRef3 from "@/assets/interior-ref-3.jpeg";
import interiorRef4 from "@/assets/interior-ref-4.jpeg";
import interiorRef5 from "@/assets/interior-ref-5.jpeg";

const references = [
  {
    category: "Fassadengestaltung",
    short: "Fassade",
    desc: "Anstrich, Sanierung und WDVS-Dämmung an Einfamilien- und Mehrfamilienhäusern.",
    images: [facadeRef1, facadeRef2, facadeRef3, facadeRef4, facadeRef5, facadeRef6, facadeRef7, facadeRef8, facadeRef9, facadeRef10]
  },
  {
    category: "Innenarbeiten",
    short: "Innen",
    desc: "Wand- und Deckengestaltung, Tapezieren, Renovierungen in Wohn- und Geschäftsräumen.",
    images: [interiorRef1, interiorRef2, interiorRef3, interiorRef4, interiorRef5]
  },
  {
    category: "Badezimmer",
    short: "Bad",
    desc: "Fugenlose Bäder mit Mikrozement, Fliesenarbeiten und Komplettsanierungen.",
    images: [bathroomRef1, bathroomRef2, bathroomRef3, bathroomRef4, bathroomRef5, bathroomRef6, bathroomRef7, bathroomRef8, bathroomRef9]
  },
  {
    category: "Edle Oberflächen",
    short: "Oberflächen",
    desc: "Spachteltechniken in Betonoptik, Marmorino und Metalloptik – handgefertigte Unikate.",
    images: [surfaceRef1, surfaceRef2, surfaceRef3, surfaceRef4, surfaceRef5, surfaceRef6, specialSurfaceImg]
  },
  {
    category: "Bodenveredelung",
    short: "Böden",
    desc: "Designböden, Mikrozement und Spachtelböden für offenes, fugenloses Wohnen.",
    images: [floorRef1, floorRef2, floorRef3, floorRef4, floorImg]
  }
];

export const References = () => {
  const [selectedCategory, setSelectedCategory] = useState(references[0].category);
  const current = references.find(ref => ref.category === selectedCategory) ?? references[0];
  const currentImages = current.images;

  // Preload all reference images once so switching categories is instant
  useEffect(() => {
    references.forEach((ref) => {
      ref.images.forEach((src) => {
        const img = new Image();
        img.src = src;
      });
    });
  }, []);

  return (
    <section id="referenzen" className="py-16 md:py-24 lg:py-32 bg-muted/20 scroll-mt-32">
      <div className="container mx-auto px-4">
        {/* Header */}
        <div className="max-w-3xl mb-12 md:mb-16">
          <div className="text-[10px] tracking-[0.3em] uppercase text-muted-foreground mb-4">
            Referenzen aus Herford & Umgebung
          </div>
          <h2 className="font-caslon text-3xl md:text-4xl lg:text-5xl lg:text-6xl font-bold leading-[1.05] mb-5">
            Sehen Sie sich unsere Arbeit an.
          </h2>
          <p className="text-base md:text-lg text-muted-foreground leading-relaxed">
            Wählen Sie einen Bereich aus – jedes Bild zeigt ein real umgesetztes Projekt aus Herford und der Region.
          </p>
        </div>

        {/* Category Tabs – clear, with counts */}
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-2 md:gap-3 mb-10">
          {references.map((ref) => {
            const active = selectedCategory === ref.category;
            return (
              <button
                key={ref.category}
                onClick={() => setSelectedCategory(ref.category)}
                aria-pressed={active}
                className={`group text-left rounded-xl border px-4 py-4 transition-all ${
                  active
                    ? "bg-foreground text-background border-foreground shadow-sm"
                    : "bg-background border-foreground/10 hover:border-foreground/30 hover:-translate-y-0.5"
                }`}
              >
                <div className="flex items-baseline gap-2">
                  <span className="font-semibold text-sm md:text-base leading-tight">
                    {ref.category}
                  </span>
                </div>
              </button>
            );
          })}
        </div>

        {/* Current category description */}
        <div
          key={`desc-${selectedCategory}`}
          className="max-w-2xl mb-8 animate-fade-in"
        >
          <p className="text-sm md:text-base text-muted-foreground leading-relaxed">
            <span className="font-medium text-foreground">{current.category}.</span>{" "}
            {current.desc}
          </p>
        </div>

        {/* Image Slider */}
        <div className="relative">
          <Carousel
            key={selectedCategory}
            opts={{ align: "start", loop: true }}
            plugins={[Autoplay({ delay: 5000, stopOnInteraction: true })]}
            className="w-full"
          >
            <CarouselContent>
              {currentImages.map((image, index) => (
                <CarouselItem key={index} className="md:basis-1/2 lg:basis-1/3">
                  <div className="p-1.5">
                    <div className="relative overflow-hidden rounded-2xl aspect-[3/4] group bg-muted">
                      <img
                        src={image}
                        alt={`${selectedCategory} Referenz ${index + 1} – Malerfachbetrieb Stein Herford`}
                        loading="eager"
                        decoding="async"
                        className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                      />
                      <div className="absolute bottom-3 left-3 inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-background/85 backdrop-blur text-[10px] tracking-[0.15em] uppercase text-foreground/80">
                        {current.short} · {String(index + 1).padStart(2, "0")} / {String(currentImages.length).padStart(2, "0")}
                      </div>
                    </div>
                  </div>
                </CarouselItem>
              ))}
            </CarouselContent>
            <CarouselPrevious className="left-2 md:-left-4" />
            <CarouselNext className="right-2 md:-right-4" />
          </Carousel>
        </div>

        {/* CTA */}
        <div className="mt-16 flex flex-col md:flex-row items-start md:items-center justify-between gap-6 max-w-4xl">
          <p className="font-caslon text-2xl md:text-3xl leading-snug max-w-xl">
            Ihr Projekt könnte hier stehen.
          </p>
          <Button
            size="lg"
            className="bg-foreground text-background hover:bg-foreground/90 rounded-full px-8"
            onClick={() => {
              const element = document.getElementById('kontakt');
              element?.scrollIntoView({ behavior: 'smooth' });
            }}
          >
            Kostenloses Angebot anfragen
          </Button>
        </div>
      </div>
    </section>
  );
};
